import React, { useContext, useMemo, useState } from 'react';
import { CheckboxWrapper, Icon, Loading } from 'venn-ui-kit';
import ChartLegend from './ChartLegend';
import { isNil } from 'lodash';
import type { Solution } from 'venn-components';
import { ContentBlock, EmptyState } from 'venn-components';
import { ThemeContext } from 'styled-components';
import { getEfficientFrontierChartConfig } from './chartConfigUtils';
import type { SolutionData } from '../../logic/useOptimizationSolutions';
import type { FactorForecast, RiskReturnPoint } from 'venn-api';
import SelectableSolutionChartSubheader from './SelectableSolutionChartSubheader';
import useSynchronizedSelectedChartPoints from './useSynchronizedSelectedChartPoints';
import { VennHighchart } from '../../../../../../venn-components/src/highchart/Highchart';
import { cn } from '@/lib/utils';

export interface EfficientFrontierCardData {
  current?: SolutionData;
  optimized?: SolutionData;
  benchmark?: SolutionData;
  alternate?: SolutionData[];
  efficientFrontier?: RiskReturnPoint[];

  summaryLoading: boolean;
  summaryError: boolean;
}

interface EfficientFrontierCardViewProps extends EfficientFrontierCardData {
  factorForecasts?: FactorForecast[];

  selectedSolution?: Solution;
  onSelectSolution: (solution: Solution) => void;
}

const EfficientFrontierCardView = ({
  current,
  optimized,
  alternate,
  benchmark,
  efficientFrontier,
  factorForecasts,
  selectedSolution,
  onSelectSolution,
  summaryLoading,
  summaryError,
}: EfficientFrontierCardViewProps) => {
  const [showFactorForecasts, setShowFactorForecasts] = useState(true);
  const theme = useContext(ThemeContext);

  const { setChartPoints } = useSynchronizedSelectedChartPoints(selectedSolution);

  const chartConfig = useMemo(() => {
    return getEfficientFrontierChartConfig(
      theme,
      onSelectSolution,
      current,
      showFactorForecasts,
      factorForecasts,
      optimized,
      efficientFrontier ?? [],
      alternate ?? [],
      benchmark,
      setChartPoints,
    );
  }, [
    theme,
    onSelectSolution,
    current,
    showFactorForecasts,
    factorForecasts,
    optimized,
    efficientFrontier,
    alternate,
    benchmark,
    setChartPoints,
  ]);

  return (
    <ContentBlock
      header="Efficient Frontier"
      subHeader={<SelectableSolutionChartSubheader />}
      className="bg-venn-white"
    >
      {summaryLoading ? (
        <div className="min-h-[430px]">
          <Loading />
        </div>
      ) : summaryError ? (
        <div className="p-[20px]">
          <EmptyState header="Unable to calculate forecast" />
        </div>
      ) : (
        <>
          <ChartLegend
            hasOptimized={!isNil(optimized)}
            hasBenchmark={!isNil(benchmark)}
            rightChildren={
              !isNil(factorForecasts) &&
              factorForecasts.length > 0 && (
                <div
                  className={cn([
                    'flex text-[10px] font-bold [&_span]:font-bold [&_span]:pl-[4px] [&_i]:mr-[2px]',
                    '[&>label]:min-w-[10px] [&>label]:min-h-[10px]',
                    '[&>label>input]:min-w-[10px] [&>label>input]:max-w-[10px] [&>label>input]:h-[10px] [&>label>input]:text-[7px]',
                  ])}
                >
                  <CheckboxWrapper
                    checked={showFactorForecasts}
                    onChange={() => setShowFactorForecasts(!showFactorForecasts)}
                  >
                    {' '}
                    <Icon type="dna" /> Factor Forecasts
                  </CheckboxWrapper>
                </div>
              )
            }
          />
          <VennHighchart immutable options={chartConfig} className="qa-efficient-frontier-chart" />
        </>
      )}
    </ContentBlock>
  );
};

export default React.memo(EfficientFrontierCardView);
